import { useState, useCallback } from 'react';
import api from '../services/api';

/**
 * Hook do zarzadzania rabatami w POS
 * Wymaga stanow appliedDiscounts i cartDiscountTotal z CartContext
 */
const useDiscounts = ({
  currentUser,
  locationId,
  // Z CartContext
  cart,
  currentTransaction,
  setCurrentTransaction,
  appliedDiscounts,
  setAppliedDiscounts,
  cartDiscountTotal,
  setCartDiscountTotal,
  // Blad
  setError
}) => {
  const [availableDiscounts, setAvailableDiscounts] = useState([]);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountLoading, setDiscountLoading] = useState(false);

  // Stany dla modala kodu weryfikacyjnego rabatu
  const [showDiscountCodeModal, setShowDiscountCodeModal] = useState(false);
  const [pendingDiscountId, setPendingDiscountId] = useState(null);
  const [discountVerificationCode, setDiscountVerificationCode] = useState('');
  const [discountCustomerCard, setDiscountCustomerCard] = useState('');
  const [discountInfluencerName, setDiscountInfluencerName] = useState('');
  const [discountCodeError, setDiscountCodeError] = useState('');

  const loadAvailableDiscounts = useCallback(async () => {
    // GUARD: nie pobieraj rabatow, dopoki nie znamy PRAWDZIWEGO uzytkownika i lokalizacji.
    // Wczesniej przy starcie lecialo zapytanie z fallbackiem 'admin' i pusta lokalizacja,
    // wiec kasjer dostawal LISTE RABATOW ADMINA - blad dotykajacy marzy, nie kosmetyka.
    // PosPage nie ma juz fallbacku (currentUser = user?.login || ""), wiec sam login
    // "admin" jest prawdziwym kontem i MUSI dostac liste - dawny warunek
    // currentUser === 'admin' blokowal wlascicielowi modal rabatow (2026-09-11).
    if (!currentUser || !locationId) {
      return;
    }
    try {
      // params (nie query string): interceptor api.js dokleja location_id tylko gdy
      // NIE ma go w config.params - w query stringu go nie widzial i dublowal parametr.
      const response = await api.get('/rabaty', { params: { user_id: currentUser, location_id: locationId } });
      const data = response.data;
      setAvailableDiscounts(data.data?.rabaty || []);
    } catch (error) {
      console.error("Blad ladowania rabatow:", error);
    }
  }, [currentUser, locationId]);

  const applyDiscount = useCallback(async (
    discountId,
    note = "",
    verificationCode = null,
    customerCard = null,
    influencerName = null
  ) => {
    if (!cart.length) {
      setError("Koszyk jest pusty");
      return;
    }

    const discount = availableDiscounts.find((d) => d.id === discountId);
    if (!discount) {
      setError("Rabat nie zostal znaleziony");
      return;
    }

    if (discount.status_dostepnosci !== "DOSTEPNY") {
      setError("Rabat nie jest dostepny (przekroczony limit)");
      return;
    }

    const safeApplied = Array.isArray(appliedDiscounts) ? appliedDiscounts : [];
    if (safeApplied.some((d) => d.id === discountId)) {
      setError("Ten rabat jest juz zastosowany");
      return;
    }

    const requiresCode = discount.wymaga_kodu === 1 || discount.wymaga_kodu === true;
    const requiresCard = discount.wymaga_karty_klienta === 1 || discount.wymaga_karty_klienta === true;
    const requiresInfluencer = discount.wymaga_influencera === 1 || discount.wymaga_influencera === true;

    if ((requiresCode || requiresCard || requiresInfluencer) && !verificationCode && !customerCard && !influencerName) {
      setPendingDiscountId(discountId);
      setDiscountVerificationCode('');
      setDiscountCustomerCard('');
      setDiscountInfluencerName('');
      setDiscountCodeError('');
      setShowDiscountCodeModal(true);
      return;
    }

    if (requiresCode && verificationCode) {
      const cleanCode = verificationCode.replace(/\D/g, '');
      if (cleanCode.length !== 12) {
        setDiscountCodeError('Kod weryfikacyjny musi miec dokladnie 12 cyfr');
        return;
      }
    }

    setDiscountLoading(true);
    try {
      let transactionId = currentTransaction?.id;

      if (!transactionId) {
        const cartData = {
          kasjer_id: currentUser,
          location_id: locationId,
          shift_id: null,
        };

        const createResponse = await api.post('/pos/cart/new', cartData);

        const createData = createResponse.data;
        transactionId = createData.data?.transakcja_id || createData.message?.transakcja_id;
        setCurrentTransaction({
          id: transactionId,
          numer_transakcji: createData.data?.numer_transakcji || createData.message?.numer_transakcji,
        });
      }

      // Usun poprzednie rabaty z backendu
      const prevDiscounts = Array.isArray(appliedDiscounts) ? appliedDiscounts : [];
      for (const oldDiscount of prevDiscounts) {
        if (oldDiscount.uzycie_id) {
          try {
            await api.delete(`/pos/cart/${transactionId}/discount/${oldDiscount.uzycie_id}`);
          } catch (e) {
            console.error("Nie udalo sie usunac poprzedniego rabatu:", e);
          }
        }
      }

      // Wyczysc pozycje
      try {
        await api.delete(`/pos/cart/${transactionId}/items/clear`);
      } catch (e) {
        console.error("Nie udalo sie wyczyscic pozycji:", e);
      }

      // Dodaj produkty
      for (const item of cart) {
        await api.post(`/pos/cart/${transactionId}/items`, {
            product_id: item.id,
            ilosc: item.quantity,
            cena_jednostkowa: item.price,
          });
      }
      // Zastosuj rabat
      const discountResponse = await api.post(`/pos/cart/${transactionId}/discount`, {
          rabat_id: discountId,
          user_id: currentUser,
          notatka: note,
          ip_address: window.location.hostname,
          kod_weryfikacyjny: verificationCode || null,
          numer_karty_klienta: customerCard || null,
          nazwa_influencera: influencerName || null,
        });

      const discountData = discountResponse.data;

      // Wyczysc poprzednie rabaty
      setAppliedDiscounts([]);
      setCartDiscountTotal(0);

      // Dodaj nowy rabat.
      // kwota_rabatu z serwera jest juz po clampie i regule grosza (ten sam
      // silnik co finalizacja); na wyswietlaczu i w platnosci kwote i tak
      // liczy NA ZYWO CartContext.getEffectiveDiscount tym samym algorytmem
      // (jedno zrodlo kwoty rabatu). max_rabat_koszyka i regula_grosza
      // zachowujemy dla UI (informacja, dlaczego rabat jest nizszy).
      const newDiscount = {
        id: discountId,
        nazwa: discount.nazwa,
        typ_rabatu: discount.typ_rabatu,
        wartosc: discount.wartosc,
        kwota_rabatu: discountData.message.kwota_rabatu,
        uzycie_id: discountData.message.uzycie_id,
        czasowe_id: Date.now(),
        kwota_do_rabatu: discountData.message.kwota_do_rabatu,
        pozostaly_limit_po: discountData.message.pozostaly_limit_po,
        max_rabat_koszyka: discountData.message.max_rabat_koszyka,
        regula_grosza: discountData.message.regula_grosza || null,
        info: discountData.message.info
      };

      setAppliedDiscounts([newDiscount]);
      setCartDiscountTotal(discountData.message.kwota_rabatu);
      setShowDiscountModal(false);

      loadAvailableDiscounts();
    } catch (error) {
      console.error("Blad zastosowania rabatu:", error);
      setError("Blad zastosowania rabatu: " + error.message);
    } finally {
      setDiscountLoading(false);
    }
  }, [availableDiscounts, appliedDiscounts, cart, currentTransaction, currentUser, locationId,
      setCurrentTransaction, setAppliedDiscounts, setCartDiscountTotal, setError, loadAvailableDiscounts]);

  const handleDiscountCodeSubmit = useCallback(async () => {
    if (!pendingDiscountId) return;

    const discount = availableDiscounts.find((d) => d.id === pendingDiscountId);
    if (!discount) {
      setDiscountCodeError('Rabat nie zostal znaleziony');
      return;
    }

    const requiresCode = discount.wymaga_kodu === 1 || discount.wymaga_kodu === true;
    const requiresCard = discount.wymaga_karty_klienta === 1 || discount.wymaga_karty_klienta === true;
    const requiresInfluencer = discount.wymaga_influencera === 1 || discount.wymaga_influencera === true;

    if (requiresCode) {
      const cleanCode = discountVerificationCode.replace(/\D/g, '');
      if (cleanCode.length !== 12) {
        setDiscountCodeError('Kod weryfikacyjny musi miec dokladnie 12 cyfr');
        return;
      }
    }

    if (requiresCard && !discountCustomerCard.trim()) {
      setDiscountCodeError('Wprowadz numer karty klienta');
      return;
    }

    if (requiresInfluencer && !discountInfluencerName.trim()) {
      setDiscountCodeError('Wprowadz nazwe influencera');
      return;
    }

    setShowDiscountCodeModal(false);
    await applyDiscount(
      pendingDiscountId,
      '',
      requiresCode ? discountVerificationCode.replace(/\D/g, '') : null,
      requiresCard ? discountCustomerCard.trim() : null,
      requiresInfluencer ? discountInfluencerName.trim() : null
    );
    setPendingDiscountId(null);
  }, [pendingDiscountId, availableDiscounts, discountVerificationCode, discountCustomerCard, discountInfluencerName, applyDiscount]);

  const removeDiscount = useCallback(async (czasoweId) => {
    const safeAppliedDiscounts = Array.isArray(appliedDiscounts) ? appliedDiscounts : [];
    const discountToRemove = safeAppliedDiscounts.find((d) => d.czasowe_id === czasoweId);

    if (!discountToRemove) {
      return;
    }

    try {
      if (discountToRemove.uzycie_id && currentTransaction?.id) {
        await api.delete(`/pos/cart/${currentTransaction.id}/discount/${discountToRemove.uzycie_id}`);
      }

      setAppliedDiscounts((prev) => prev.filter((d) => d.czasowe_id !== czasoweId));
      setCartDiscountTotal((prev) => prev - discountToRemove.kwota_rabatu);

      loadAvailableDiscounts();
    } catch (error) {
      console.error("Blad usuwania rabatu:", error);
      setError("Blad usuwania rabatu: " + error.message);
    }
  }, [appliedDiscounts, currentTransaction, setAppliedDiscounts, setCartDiscountTotal, setError, loadAvailableDiscounts]);

  return {
    // Stan
    availableDiscounts,
    showDiscountModal,
    setShowDiscountModal,
    discountLoading,

    // Modal kodu
    showDiscountCodeModal,
    setShowDiscountCodeModal,
    pendingDiscountId,
    setPendingDiscountId,
    discountVerificationCode,
    setDiscountVerificationCode,
    discountCustomerCard,
    setDiscountCustomerCard,
    discountInfluencerName,
    setDiscountInfluencerName,
    discountCodeError,
    setDiscountCodeError,

    // Akcje
    loadAvailableDiscounts,
    applyDiscount,
    handleDiscountCodeSubmit,
    removeDiscount
  };
};

export default useDiscounts;
